/* eslint-disable */
import { useMutation, useQuery } from '@tanstack/react-query';
import type { QueryKey, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

import { accountFetcher } from '../Fetcher';
import type { RequestOptions } from '../Fetcher';
const fetcher = accountFetcher;

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
  errorCode: string;
};

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
  errorCode: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export const API_ENDPOINT = {
  CLIENT_V1_ACCOUNTS_SLUG_CONFIRM: '/client/api/v1/accounts/slug/confirm',
  CLIENT_V1_ACCOUNTS_MY_PROFILE: '/client/api/v1/accounts/my/profile',
  CLIENT_V1_ACCOUNTS_PROFILE_IMAGE: '/client/api/v1/accounts/profile-image',
  CLIENT_V1_ACCOUNTS_CREDENTIALS: '/client/api/v1/accounts/credentials',
  CLIENT_V1_ACCOUNTS_INVENTORY: '/client/api/v1/accounts/inventory',
  CLIENT_V1_ACCOUNTS_NOTIFICATION: '/client/api/v1/accounts/notification',
  CLIENT_V1_ACCOUNTS_NOTIFICATION_PUBLIC: '/client/api/v1/accounts/notification/public',
  CLIENT_V2_ACCOUNTS_PROFILE: '/client/api/v2/accounts/profile',
  CLIENT_V1_SESSIONS: '/client/api/v1/sessions',
  CLIENT_V1_AUTH_SIGNOUT: '/client/api/v1/auth/signout',
  CLIENT_V1_AUTH_WITHDRAW: '/client/api/v1/auth/withdraw',
  CLIENT_V1_AUTH_CHANGE_EMAIL_VERIFICATION: '/client/api/v1/auth/change/email/verification',
  CLIENT_V1_AUTH_CHANGE_EMAIL: '/client/api/v1/auth/change/email',
  CLIENT_V1_AUTH_CHANGE_PASSWORD_VERIFICATION_EMAIL:
    '/client/api/v1/auth/change/password/verification/email',
  CLIENT_V1_AUTH_CHANGE_PASSWORD_VERIFICATION_TOKEN:
    '/client/api/v1/auth/change/password/verification/token',
  CLIENT_V1_AUTH_CHANGE_PASSWORD_BY_TOKEN: '/client/api/v1/auth/change/password/by-token',
  CLIENT_V1_AUTH_CHANGE_PASSWORD: '/client/api/v1/auth/change/password',
  CLIENT_V1_AUTH_PHONE_VERIFICATION: '/client/api/v1/auth/phone-verification',
  CLIENT_V1_AUTH_PHONE_VERIFICATION_PUBLIC: '/client/api/v1/auth/phone-verification/public',
  CLIENT_V1_AUTH_PHONE_VERIFICATION_CHECK: '/client/api/v1/auth/phone-verification/check',
  CLIENT_V1_AUTH_CHANGE_PHONE_NUMBER: '/client/api/v1/auth/change/phone-number',
  CLIENT_V1_AUTH_SSO_REDIRECT: '/client/api/v1/auth/sso-redirect',
  CLIENT_V1_AUTH_SIGNUP_VERIFY: '/client/api/v1/auth/signup/verify',
  CLIENT_V2_AUTH_SIGNIN: '/client/api/v2/auth/signin',
  CLIENT_V2_AUTH_SIGNUP: '/client/api/v2/auth/signup',
  CLIENT_V2_AUTH_SIGNUP_VERIFY_EMAIL: '/client/api/v2/auth/signup/verify/email',
  CLIENT_V1_DASHBOARDS_RECENT_LEARNING: '/client/api/v1/dashboards/recent-learning',
  CLIENT_V1_DASHBOARDS_MY_NOTES: '/client/api/v1/dashboards/my-notes',
  CLIENT_V1_DASHBOARDS_MY_QUESTIONS: '/client/api/v1/dashboards/my-questions',
  CLIENT_V2_DASHBOARD_ANNUAL_LEARNING: '/client/api/v2/dashboard/annual-learning',
  CLIENT_V2_DASHBOARD_CERTIFICATES: '/client/api/v2/dashboard/certificates',
  CLIENT_V2_DASHBOARD_SKILLS: '/client/api/v2/dashboard/skills',
  CLIENT_V2_DASHBOARD_JOB_POSTS: '/client/api/v2/dashboard/job-posts',
  CLIENT_V2_DASHBOARD_WEEKLY: '/client/api/v2/dashboard/weekly',
  CLIENT_V2_DASHBOARD_ROADMAPS: '/client/api/v2/dashboard/roadmaps',
  CLIENT_V1_COACHES_WEEKLY_GOAL: '/client/api/v1/coaches/weekly-goal',
  CLIENT_V1_USERS_ACTIVITY_LOGS: '/client/api/v1/users/activity-logs',
  CLIENT_V1_USERS_ACTIVITY_LOGS_COURSES: '/client/api/v1/users/activity-logs/courses',
  CLIENT_V1_ACTIVITY_LOGS_COURSES_COURSEID: (courseId: string) =>
    `/client/api/v1/activity-logs/courses/${courseId}`,
  CLIENT_V1_ACTIVITY_LOGS_COURSES_COURSEID_MESSAGES_MESSAGEID: (
    messageId: string,
    courseId: string,
  ) => `/client/api/v1/activity-logs/courses/${courseId}/messages/${messageId}`,
  CLIENT_V1_ACTIVITY_LOGS_CATEGORIES: '/client/api/v1/activity-logs/categories',
  CLIENT_V1_GUIDES: '/client/api/v1/guides',
  CLIENT_V1_GAMES_RESULTS: '/client/api/v1/games/results',
  CLIENT_V1_GAMES_RESULTS_RESULTID: (resultId: number) =>
    `/client/api/v1/games/results/${resultId}`,
  CLIENT_V1_GAMES_STATS: '/client/api/v1/games/stats',
} as const;
export function useClientApiV1AccountsSlugConfirmUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AccountsSlugConfirmUpdateResponse,
    unknown,
    ClientApiV1AccountsSlugConfirmUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AccountsSlugConfirmUpdateResponse,
    unknown,
    ClientApiV1AccountsSlugConfirmUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AccountsSlugConfirmUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AccountsMyProfileUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AccountsMyProfileUpdateResponse,
    unknown,
    ClientApiV1AccountsMyProfileUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AccountsMyProfileUpdateResponse,
    unknown,
    ClientApiV1AccountsMyProfileUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AccountsMyProfileUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AccountsProfileImageCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AccountsProfileImageCreateResponse,
    unknown,
    ClientApiV1AccountsProfileImageCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AccountsProfileImageCreateResponse,
    unknown,
    ClientApiV1AccountsProfileImageCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AccountsProfileImageCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AccountsNotificationUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AccountsNotificationUpdateResponse,
    unknown,
    ClientApiV1AccountsNotificationUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AccountsNotificationUpdateResponse,
    unknown,
    ClientApiV1AccountsNotificationUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AccountsNotificationUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AccountsNotificationPublicUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AccountsNotificationPublicUpdateResponse,
    unknown,
    ClientApiV1AccountsNotificationPublicUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AccountsNotificationPublicUpdateResponse,
    unknown,
    ClientApiV1AccountsNotificationPublicUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AccountsNotificationPublicUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthWithdrawCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthWithdrawCreateResponse,
    unknown,
    ClientApiV1AuthWithdrawCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthWithdrawCreateResponse,
    unknown,
    ClientApiV1AuthWithdrawCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthWithdrawCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangeEmailVerificationCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangeEmailVerificationCreateResponse,
    unknown,
    ClientApiV1AuthChangeEmailVerificationCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangeEmailVerificationCreateResponse,
    unknown,
    ClientApiV1AuthChangeEmailVerificationCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangeEmailVerificationCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangeEmailCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangeEmailCreateResponse,
    unknown,
    ClientApiV1AuthChangeEmailCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangeEmailCreateResponse,
    unknown,
    ClientApiV1AuthChangeEmailCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangeEmailCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangePasswordVerificationEmailCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangePasswordVerificationEmailCreateResponse,
    unknown,
    ClientApiV1AuthChangePasswordVerificationEmailCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangePasswordVerificationEmailCreateResponse,
    unknown,
    ClientApiV1AuthChangePasswordVerificationEmailCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangePasswordVerificationEmailCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangePasswordVerificationTokenCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangePasswordVerificationTokenCreateResponse,
    unknown,
    ClientApiV1AuthChangePasswordVerificationTokenCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangePasswordVerificationTokenCreateResponse,
    unknown,
    ClientApiV1AuthChangePasswordVerificationTokenCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangePasswordVerificationTokenCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangePasswordByTokenUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangePasswordByTokenUpdateResponse,
    unknown,
    ClientApiV1AuthChangePasswordByTokenUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangePasswordByTokenUpdateResponse,
    unknown,
    ClientApiV1AuthChangePasswordByTokenUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangePasswordByTokenUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangePasswordUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangePasswordUpdateResponse,
    unknown,
    ClientApiV1AuthChangePasswordUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangePasswordUpdateResponse,
    unknown,
    ClientApiV1AuthChangePasswordUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangePasswordUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthPhoneVerificationCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthPhoneVerificationCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthPhoneVerificationCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthPhoneVerificationCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthPhoneVerificationPublicCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthPhoneVerificationPublicCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationPublicCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthPhoneVerificationPublicCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationPublicCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthPhoneVerificationPublicCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthPhoneVerificationCheckCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthPhoneVerificationCheckCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationCheckCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthPhoneVerificationCheckCreateResponse,
    unknown,
    ClientApiV1AuthPhoneVerificationCheckCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthPhoneVerificationCheckCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthChangePhoneNumberCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthChangePhoneNumberCreateResponse,
    unknown,
    ClientApiV1AuthChangePhoneNumberCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthChangePhoneNumberCreateResponse,
    unknown,
    ClientApiV1AuthChangePhoneNumberCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthChangePhoneNumberCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1AuthSignupVerifyCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1AuthSignupVerifyCreateResponse,
    unknown,
    ClientApiV1AuthSignupVerifyCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1AuthSignupVerifyCreateResponse,
    unknown,
    ClientApiV1AuthSignupVerifyCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1AuthSignupVerifyCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV2AuthSigninCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV2AuthSigninCreateResponse,
    unknown,
    ClientApiV2AuthSigninCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV2AuthSigninCreateResponse,
    unknown,
    ClientApiV2AuthSigninCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV2AuthSigninCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV2AuthSignupCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV2AuthSignupCreateResponse,
    unknown,
    ClientApiV2AuthSignupCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV2AuthSignupCreateResponse,
    unknown,
    ClientApiV2AuthSignupCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV2AuthSignupCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV2AuthSignupVerifyEmailCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV2AuthSignupVerifyEmailCreateResponse,
    unknown,
    ClientApiV2AuthSignupVerifyEmailCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV2AuthSignupVerifyEmailCreateResponse,
    unknown,
    ClientApiV2AuthSignupVerifyEmailCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV2AuthSignupVerifyEmailCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1CoachesWeeklyGoalUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1CoachesWeeklyGoalUpdateResponse,
    unknown,
    ClientApiV1CoachesWeeklyGoalUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1CoachesWeeklyGoalUpdateResponse,
    unknown,
    ClientApiV1CoachesWeeklyGoalUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1CoachesWeeklyGoalUpdateVariables) =>
      fetcher.patch(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteResponse,
    unknown,
    ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteResponse,
    unknown,
    ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteVariables,
    TContext
  >({
    mutationFn: (
      variables: ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteVariables,
    ) => fetcher.delete(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1GamesResultsCreateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1GamesResultsCreateResponse,
    unknown,
    ClientApiV1GamesResultsCreateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1GamesResultsCreateResponse,
    unknown,
    ClientApiV1GamesResultsCreateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1GamesResultsCreateVariables) =>
      fetcher.post(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}
export function useClientApiV1GamesResultsResultIdUpdateMutation<TContext>({
  url,
  options,
  requestOptions,
}: {
  url: string;
  options?: UseMutationOptions<
    ClientApiV1GamesResultsResultIdUpdateResponse,
    unknown,
    ClientApiV1GamesResultsResultIdUpdateVariables,
    TContext
  >;
  requestOptions?: RequestOptions;
}) {
  const result = useMutation<
    ClientApiV1GamesResultsResultIdUpdateResponse,
    unknown,
    ClientApiV1GamesResultsResultIdUpdateVariables,
    TContext
  >({
    mutationFn: (variables: ClientApiV1GamesResultsResultIdUpdateVariables) =>
      fetcher.put(url, variables, requestOptions),
    ...options,
  });

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AccountsMyProfile = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AccountsMyProfileResponse>(url, requestOptions);

export interface ClientApiV1AccountsMyProfileQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AccountsMyProfileResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AccountsMyProfileQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AccountsMyProfileQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AccountsMyProfile(url, requestOptions),
  ...options,
});

export function useClientApiV1AccountsMyProfileQuery(
  params: ClientApiV1AccountsMyProfileQueryParams,
) {
  const result = useQuery<ClientApiV1AccountsMyProfileResponse, unknown>(
    getClientApiV1AccountsMyProfileQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AccountsCredentials = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AccountsCredentialsResponse>(url, requestOptions);

export interface ClientApiV1AccountsCredentialsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AccountsCredentialsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AccountsCredentialsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AccountsCredentialsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AccountsCredentials(url, requestOptions),
  ...options,
});

export function useClientApiV1AccountsCredentialsQuery(
  params: ClientApiV1AccountsCredentialsQueryParams,
) {
  const result = useQuery<ClientApiV1AccountsCredentialsResponse, unknown>(
    getClientApiV1AccountsCredentialsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AccountsInventory = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AccountsInventoryResponse>(url, requestOptions);

export interface ClientApiV1AccountsInventoryQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AccountsInventoryResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AccountsInventoryQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AccountsInventoryQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AccountsInventory(url, requestOptions),
  ...options,
});

export function useClientApiV1AccountsInventoryQuery(
  params: ClientApiV1AccountsInventoryQueryParams,
) {
  const result = useQuery<ClientApiV1AccountsInventoryResponse, unknown>(
    getClientApiV1AccountsInventoryQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AccountsNotification = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AccountsNotificationResponse>(url, requestOptions);

export interface ClientApiV1AccountsNotificationQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AccountsNotificationResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AccountsNotificationQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AccountsNotificationQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AccountsNotification(url, requestOptions),
  ...options,
});

export function useClientApiV1AccountsNotificationQuery(
  params: ClientApiV1AccountsNotificationQueryParams,
) {
  const result = useQuery<ClientApiV1AccountsNotificationResponse, unknown>(
    getClientApiV1AccountsNotificationQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AccountsNotificationPublic = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AccountsNotificationPublicResponse>(url, requestOptions);

export interface ClientApiV1AccountsNotificationPublicQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AccountsNotificationPublicResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AccountsNotificationPublicQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AccountsNotificationPublicQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AccountsNotificationPublic(url, requestOptions),
  ...options,
});

export function useClientApiV1AccountsNotificationPublicQuery(
  params: ClientApiV1AccountsNotificationPublicQueryParams,
) {
  const result = useQuery<ClientApiV1AccountsNotificationPublicResponse, unknown>(
    getClientApiV1AccountsNotificationPublicQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2AccountsProfile = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2AccountsProfileResponse>(url, requestOptions);

export interface ClientApiV2AccountsProfileQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2AccountsProfileResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2AccountsProfileQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2AccountsProfileQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2AccountsProfile(url, requestOptions),
  ...options,
});

export function useClientApiV2AccountsProfileQuery(params: ClientApiV2AccountsProfileQueryParams) {
  const result = useQuery<ClientApiV2AccountsProfileResponse, unknown>(
    getClientApiV2AccountsProfileQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1Sessions = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1SessionsResponse>(url, requestOptions);

export interface ClientApiV1SessionsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1SessionsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1SessionsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1SessionsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1Sessions(url, requestOptions),
  ...options,
});

export function useClientApiV1SessionsQuery(params: ClientApiV1SessionsQueryParams) {
  const result = useQuery<ClientApiV1SessionsResponse, unknown>(
    getClientApiV1SessionsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AuthSignout = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1AuthSignoutResponse>(url, requestOptions);

export interface ClientApiV1AuthSignoutQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1AuthSignoutResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AuthSignoutQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AuthSignoutQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AuthSignout(url, requestOptions),
  ...options,
});

export function useClientApiV1AuthSignoutQuery(params: ClientApiV1AuthSignoutQueryParams) {
  const result = useQuery<ClientApiV1AuthSignoutResponse, unknown>(
    getClientApiV1AuthSignoutQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1AuthSsoRedirect = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1AuthSsoRedirectResponse>(url, requestOptions);

export interface ClientApiV1AuthSsoRedirectQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1AuthSsoRedirectResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1AuthSsoRedirectQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1AuthSsoRedirectQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1AuthSsoRedirect(url, requestOptions),
  ...options,
});

export function useClientApiV1AuthSsoRedirectQuery(params: ClientApiV1AuthSsoRedirectQueryParams) {
  const result = useQuery<ClientApiV1AuthSsoRedirectResponse, unknown>(
    getClientApiV1AuthSsoRedirectQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1DashboardsRecentLearning = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1DashboardsRecentLearningResponse>(url, requestOptions);

export interface ClientApiV1DashboardsRecentLearningQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1DashboardsRecentLearningResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1DashboardsRecentLearningQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1DashboardsRecentLearningQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1DashboardsRecentLearning(url, requestOptions),
  ...options,
});

export function useClientApiV1DashboardsRecentLearningQuery(
  params: ClientApiV1DashboardsRecentLearningQueryParams,
) {
  const result = useQuery<ClientApiV1DashboardsRecentLearningResponse, unknown>(
    getClientApiV1DashboardsRecentLearningQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1DashboardsMyNotes = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1DashboardsMyNotesResponse>(url, requestOptions);

export interface ClientApiV1DashboardsMyNotesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1DashboardsMyNotesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1DashboardsMyNotesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1DashboardsMyNotesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1DashboardsMyNotes(url, requestOptions),
  ...options,
});

export function useClientApiV1DashboardsMyNotesQuery(
  params: ClientApiV1DashboardsMyNotesQueryParams,
) {
  const result = useQuery<ClientApiV1DashboardsMyNotesResponse, unknown>(
    getClientApiV1DashboardsMyNotesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1DashboardsMyQuestions = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1DashboardsMyQuestionsResponse>(url, requestOptions);

export interface ClientApiV1DashboardsMyQuestionsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1DashboardsMyQuestionsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1DashboardsMyQuestionsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1DashboardsMyQuestionsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1DashboardsMyQuestions(url, requestOptions),
  ...options,
});

export function useClientApiV1DashboardsMyQuestionsQuery(
  params: ClientApiV1DashboardsMyQuestionsQueryParams,
) {
  const result = useQuery<ClientApiV1DashboardsMyQuestionsResponse, unknown>(
    getClientApiV1DashboardsMyQuestionsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardAnnualLearning = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardAnnualLearningResponse>(url, requestOptions);

export interface ClientApiV2DashboardAnnualLearningQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardAnnualLearningResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardAnnualLearningQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardAnnualLearningQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardAnnualLearning(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardAnnualLearningQuery(
  params: ClientApiV2DashboardAnnualLearningQueryParams,
) {
  const result = useQuery<ClientApiV2DashboardAnnualLearningResponse, unknown>(
    getClientApiV2DashboardAnnualLearningQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardCertificates = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardCertificatesResponse>(url, requestOptions);

export interface ClientApiV2DashboardCertificatesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardCertificatesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardCertificatesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardCertificatesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardCertificates(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardCertificatesQuery(
  params: ClientApiV2DashboardCertificatesQueryParams,
) {
  const result = useQuery<ClientApiV2DashboardCertificatesResponse, unknown>(
    getClientApiV2DashboardCertificatesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardSkills = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardSkillsResponse>(url, requestOptions);

export interface ClientApiV2DashboardSkillsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardSkillsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardSkillsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardSkillsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardSkills(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardSkillsQuery(params: ClientApiV2DashboardSkillsQueryParams) {
  const result = useQuery<ClientApiV2DashboardSkillsResponse, unknown>(
    getClientApiV2DashboardSkillsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardJobPosts = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardJobPostsResponse>(url, requestOptions);

export interface ClientApiV2DashboardJobPostsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardJobPostsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardJobPostsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardJobPostsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardJobPosts(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardJobPostsQuery(
  params: ClientApiV2DashboardJobPostsQueryParams,
) {
  const result = useQuery<ClientApiV2DashboardJobPostsResponse, unknown>(
    getClientApiV2DashboardJobPostsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardWeekly = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardWeeklyResponse>(url, requestOptions);

export interface ClientApiV2DashboardWeeklyQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardWeeklyResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardWeeklyQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardWeeklyQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardWeekly(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardWeeklyQuery(params: ClientApiV2DashboardWeeklyQueryParams) {
  const result = useQuery<ClientApiV2DashboardWeeklyResponse, unknown>(
    getClientApiV2DashboardWeeklyQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV2DashboardRoadmaps = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV2DashboardRoadmapsResponse>(url, requestOptions);

export interface ClientApiV2DashboardRoadmapsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV2DashboardRoadmapsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV2DashboardRoadmapsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV2DashboardRoadmapsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV2DashboardRoadmaps(url, requestOptions),
  ...options,
});

export function useClientApiV2DashboardRoadmapsQuery(
  params: ClientApiV2DashboardRoadmapsQueryParams,
) {
  const result = useQuery<ClientApiV2DashboardRoadmapsResponse, unknown>(
    getClientApiV2DashboardRoadmapsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1CoachesWeeklyGoal = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1CoachesWeeklyGoalResponse>(url, requestOptions);

export interface ClientApiV1CoachesWeeklyGoalQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1CoachesWeeklyGoalResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1CoachesWeeklyGoalQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1CoachesWeeklyGoalQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1CoachesWeeklyGoal(url, requestOptions),
  ...options,
});

export function useClientApiV1CoachesWeeklyGoalQuery(
  params: ClientApiV1CoachesWeeklyGoalQueryParams,
) {
  const result = useQuery<ClientApiV1CoachesWeeklyGoalResponse, unknown>(
    getClientApiV1CoachesWeeklyGoalQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersActivityLogs = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UsersActivityLogsResponse>(url, requestOptions);

export interface ClientApiV1UsersActivityLogsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UsersActivityLogsResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersActivityLogsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersActivityLogsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersActivityLogs(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersActivityLogsQuery(
  params: ClientApiV1UsersActivityLogsQueryParams,
) {
  const result = useQuery<ClientApiV1UsersActivityLogsResponse, unknown>(
    getClientApiV1UsersActivityLogsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1UsersActivityLogsCourses = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1UsersActivityLogsCoursesResponse>(url, requestOptions);

export interface ClientApiV1UsersActivityLogsCoursesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1UsersActivityLogsCoursesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1UsersActivityLogsCoursesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1UsersActivityLogsCoursesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1UsersActivityLogsCourses(url, requestOptions),
  ...options,
});

export function useClientApiV1UsersActivityLogsCoursesQuery(
  params: ClientApiV1UsersActivityLogsCoursesQueryParams,
) {
  const result = useQuery<ClientApiV1UsersActivityLogsCoursesResponse, unknown>(
    getClientApiV1UsersActivityLogsCoursesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ActivityLogsCoursesCourseId = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ActivityLogsCoursesCourseIdResponse>(url, requestOptions);

export interface ClientApiV1ActivityLogsCoursesCourseIdQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ActivityLogsCoursesCourseIdResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ActivityLogsCoursesCourseIdQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ActivityLogsCoursesCourseIdQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ActivityLogsCoursesCourseId(url, requestOptions),
  ...options,
});

export function useClientApiV1ActivityLogsCoursesCourseIdQuery(
  params: ClientApiV1ActivityLogsCoursesCourseIdQueryParams,
) {
  const result = useQuery<ClientApiV1ActivityLogsCoursesCourseIdResponse, unknown>(
    getClientApiV1ActivityLogsCoursesCourseIdQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1ActivityLogsCategories = async (
  url: string,
  requestOptions?: RequestOptions,
) => fetcher.get<ClientApiV1ActivityLogsCategoriesResponse>(url, requestOptions);

export interface ClientApiV1ActivityLogsCategoriesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<ClientApiV1ActivityLogsCategoriesResponse, unknown>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}

export const getClientApiV1ActivityLogsCategoriesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1ActivityLogsCategoriesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1ActivityLogsCategories(url, requestOptions),
  ...options,
});

export function useClientApiV1ActivityLogsCategoriesQuery(
  params: ClientApiV1ActivityLogsCategoriesQueryParams,
) {
  const result = useQuery<ClientApiV1ActivityLogsCategoriesResponse, unknown>(
    getClientApiV1ActivityLogsCategoriesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1Guides = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1GuidesResponse>(url, requestOptions);

export interface ClientApiV1GuidesQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1GuidesResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1GuidesQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1GuidesQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1Guides(url, requestOptions),
  ...options,
});

export function useClientApiV1GuidesQuery(params: ClientApiV1GuidesQueryParams) {
  const result = useQuery<ClientApiV1GuidesResponse, unknown>(
    getClientApiV1GuidesQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

export const fetchClientApiV1GamesStats = async (url: string, requestOptions?: RequestOptions) =>
  fetcher.get<ClientApiV1GamesStatsResponse>(url, requestOptions);

export interface ClientApiV1GamesStatsQueryParams {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<ClientApiV1GamesStatsResponse, unknown>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}

export const getClientApiV1GamesStatsQueryOptions = ({
  queryKey,
  url,
  options,
  requestOptions,
}: ClientApiV1GamesStatsQueryParams) => ({
  queryKey: queryKey,
  queryFn: () => fetchClientApiV1GamesStats(url, requestOptions),
  ...options,
});

export function useClientApiV1GamesStatsQuery(params: ClientApiV1GamesStatsQueryParams) {
  const result = useQuery<ClientApiV1GamesStatsResponse, unknown>(
    getClientApiV1GamesStatsQueryOptions(params),
  );

  return { ...result, data: result.data?.data };
}

/**
 * created by ResponseSchemaGenerator
 */

export interface ClientApiV1AccountsSlugConfirmUpdate {
  data: Maybe<String>;
}
export interface String {
  [key: string]: never;
}

export interface ClientApiV1AccountsMyProfile {
  data: Maybe<MyProfileResponse>;
}
export interface MyProfileResponse {
  /**
   * 계정(users) ID
   */
  id: number;
  /**
   * 닉네임
   */
  nickname: string;
  /**
   * users slug(고유 프로필 주소에 사용됨)
   */
  slug: null | string;
  /**
   * 자기소개
   */
  introduce: null | string;
  /**
   * 프로필 이미지 URL
   */
  profileImageUrl: string;
  /**
   * 프로필 이미지 파일 ID
   */
  profileImageFileId: null | number;
}
export interface ClientApiV1AccountsMyProfileUpdate {
  data: Maybe<String>;
}
export interface ClientApiV1AccountsProfileImageCreate {
  data: Maybe<ProfileImageUploadResponse>;
}
export interface ProfileImageUploadResponse {
  /**
   * 프로필 이미지 파일 ID
   */
  fileId: number;
  /**
   * 프로필 이미지 CDN URL
   */
  cdnUrl: string;
}
export interface ClientApiV1AccountsCredentials {
  data: Maybe<CredentialResponse>;
}
export interface CredentialResponse {
  id: number;
  email: string;
  phoneNumber: string;
  countryCode: string;
  hasPassword: boolean;
}
export interface ClientApiV1AccountsInventory {
  data: Maybe<AccountInventoryResponseDto>;
}
export interface AccountInventoryResponseDto {
  /**
   * 보유한 쿠폰 개수 (부활 가능한 쿠폰 수량에서 제외)
   */
  couponCount: number;
  /**
   * 유효한 상태의 수강권 개수
   */
  voucherCount: number;
  /**
   * 보유한 포인트
   */
  point: number;
  /**
   * 미정산금 보유 여부
   */
  hasRemainSettlementAmount?: boolean;
}
export interface ClientApiV1AccountsNotification {
  data: Maybe<MyNotificationSettingResponseDto>;
}
export interface MyNotificationSettingResponseDto {
  /**
   * 강의 새소식 알림 수신 동의 여부
   */
  courseNewNews: boolean;
  /**
   * 서비스 공지 알림 수신 동의 여부
   */
  serviceAnnouncement: boolean;
  /**
   * 마케팅 알림 수신 동의 여부
   */
  marketing: boolean;
  /**
   * 커뮤니티 댓글 알림 수신 동의 여부
   */
  communityComment: boolean;
}
export interface ClientApiV1AccountsNotificationUpdate {
  data: Maybe<ResponseEntity>;
}
export interface ResponseEntity {
  statusCode: string;
  message: string;
  data: Maybe<never>;
  errorCode: string;
}
export interface ClientApiV1AccountsNotificationPublic {
  data: Maybe<MyNotificationSettingResponseDto>;
}
export interface ClientApiV1AccountsNotificationPublicUpdate {
  data: Maybe<ResponseEntity>;
}
export type MarketingPartnerType = 'BUSINESS' | 'PERSONAL';
export type MarketingPartnerStatus = 'PENDING' | 'APPROVED';
export interface ClientApiV2AccountsProfile {
  data: Maybe<AccountProfileResponseV2Dto>;
}
export interface AccountProfileResponseV2Dto {
  /**
   * 계정 고유번호
   */
  id: number;
  /**
   * 닉네임
   */
  name: string;
  /**
   * 이메일
   */
  email: string;
  /**
   * 가입 일자
   */
  joinedAt: string;
  /**
   * 가입 일자 UTC
   */
  joinedAtUTC: string;
  /**
   * 프로필 사진 URL
   */
  thumbnail: string;
  /**
   * 휴면 일자
   */
  inactiveAt?: string;
  /**
   * 휴면 일자 UTC
   */
  inactiveAtUTC?: string;
  /**
   * 유저 실명
   */
  realname?: string;
  /**
   * 블랙유저여부
   */
  isBlocked: boolean;
  /**
   * 휴대폰 번호
   */
  phone?: PhoneResponseDto;
  /**
   * 알림 정보
   */
  notification: NotificationPreferenceResponseDto;
  /**
   * 속한 그룹 정보
   */
  group?: GroupResponseDto;
  /**
   * 관리하는 그룹 정보
   */
  managedGroup?: ManagedGroupResponseDto;
  /**
   * 마케팅 파트너 정보
   */
  marketingPartner?: MarketingPartnerResponseDto;
  /**
   * 어드민 여부
   */
  isAdmin: boolean;
  /**
   * 지식공유자 여부
   */
  isInstructor: boolean;
  /**
   * 최근 접속 일자
   */
  accessedAt?: string;
  /**
   * 최근 접속 일자 UTC
   */
  accessedAtUTC?: string;
  /**
   * 생성 방식
   */
  createdBy?: 'SELF' | 'COMPANY';
  /**
   * 유저 프로필 페이지 slug
   */
  slug?: string;
  /**
   * slug 확정 여부
   */
  isSlugConfirmed: boolean;
}
export interface PhoneResponseDto {
  number: never;
  countryCode?: string;
}
export interface NotificationPreferenceResponseDto {
  isAllowMarketing: boolean;
  isAllowNewsEmail: boolean;
  isAllowServiceAnnouncement: boolean;
  isAllowCommunityComment: boolean;
}
export interface GroupResponseDto {
  id: number;
  name: string;
  enrolmentApprovedRequired: boolean;
  isCompleteHidden: boolean;
  logoUrl?: string;
}
export interface ManagedGroupResponseDto {
  id: number;
  name: string;
  logoUrl?: string;
}
export interface MarketingPartnerResponseDto {
  /**
   * 마케팅 파트너 ID
   */
  id: number;
  type: MarketingPartnerType;
  status: MarketingPartnerStatus;
}
export interface ClientApiV1Sessions {
  data: Maybe<SessionResponse>;
}
export interface SessionResponse {
  accountId: number;
}
export interface ClientApiV1AuthSignout {
  data: Maybe<never>;
}
export interface ClientApiV1AuthWithdrawCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthChangeEmailVerificationCreate {
  data: Maybe<never>;
}
export interface ClientApiV1AuthChangeEmailCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthChangePasswordVerificationEmailCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthChangePasswordVerificationTokenCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthChangePasswordByTokenUpdate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthChangePasswordUpdate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthPhoneVerificationCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthPhoneVerificationPublicCreate {
  data: Maybe<String>;
}
export interface ClientApiV1AuthPhoneVerificationCheckCreate {
  data: CheckPhoneVerificationCodeResponse[];
}
export interface CheckPhoneVerificationCodeResponse {
  /**
   * 이메일
   */
  email: string;
  /**
   * 소셜 로그인 제공자 목록
   */
  providers: ('Apple' | 'Facebook' | 'Github' | 'Google' | 'Kakao' | 'Inflearn')[];
  /**
   * 접속 일자
   */
  accessedAt: string;
  /**
   * 접속 일자 UTC
   */
  accessedAtUTC: string;
}
export interface ClientApiV1AuthChangePhoneNumberCreate {
  data: Maybe<ChangePhoneNumberResponse>;
}
export interface ChangePhoneNumberResponse {
  /**
   * 기존에 동일 번호로 인증된 계정에서 구매 내역이 있어, 현재 계정에서 신규가입 할인이 제거되었는지 여부
   */
  isSignUpDiscountDeleted: boolean;
}
export interface ClientApiV1AuthSsoRedirect {
  data: Maybe<SsoRedirectResponse>;
}
export interface SsoRedirectResponse {
  /**
   * 리다이렉트할 SSO URL
   */
  redirectUrl: string;
}
export interface ClientApiV1AuthSignupVerifyCreate {
  data: Maybe<String>;
}
export interface ClientApiV2AuthSigninCreate {
  data: Maybe<SignInEmailAndPasswordResponseV2>;
}
export interface SignInEmailAndPasswordResponseV2 {
  /**
   * 사용자 ID
   */
  userId: number;
}
export interface ClientApiV2AuthSignupCreate {
  data: Maybe<never>;
}
export interface ClientApiV2AuthSignupVerifyEmailCreate {
  data: Maybe<never>;
}
export interface ClientApiV1DashboardsRecentLearning {
  data: Maybe<RecentLearningResponse>;
}
export interface RecentLearningResponse {
  progress: string;
  completeCount: number;
  accessedAt: string;
  accessedAtUTC: string;
  unitId: number;
  createdAt: string;
  createdAtUTC: string;
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  courseThumbnailUrl: string;
  lectureCount: number;
}
export interface ClientApiV1DashboardsMyNotes {
  data: MyNoteResponse[];
}
export interface MyNoteResponse {
  id: number;
  courseTitle: string;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV1DashboardsMyQuestions {
  data: MyQuestionsResponse[];
}
export interface MyQuestionsResponse {
  id: number;
  title: string;
  slug: string;
  createdAt: string;
  createdAtUTC: string;
}
export interface ClientApiV2DashboardAnnualLearning {
  data: Maybe<AnnualLearningResponseDto>;
}
export interface AnnualLearningResponseDto {
  /**
   * 작성한 노트 개수
   */
  noteCount: number;
  /**
   * 완강한 강의(Course) 개수
   */
  completedCourseCount: number;
  /**
   * 시청 완료한 유닛 재생 시간의 합
   */
  learningTime: TimeDto;
  /**
   * 시청 완료한 수업(Unit)의 개수
   */
  completedUnitCount: number;
  /**
   * 연간 학습 히트맵 요소값 배열
   */
  heatMap: unknown[][];
  /**
   * 전년대비 학습 시간 증감률
   */
  learningTimeAscent: null | number;
  /**
   * 전년대비 시청 완료한 수업 증감률
   */
  completedUnitAscentRate: null | number;
}
export interface TimeDto {
  /**
   * 일
   */
  days: number;
  /**
   * 시
   */
  hours: number;
  /**
   * 분
   */
  minutes: number;
}
export interface ClientApiV2DashboardCertificates {
  data: UserCertificatesResponseDto[];
}
export interface UserCertificatesResponseDto {
  /**
   * 강의 제목
   */
  title: string;
  /**
   * 수강권 ID
   */
  voucherId: number;
  /**
   * 학습 최초 시작일
   */
  startedDate: string;
  /**
   * 수강 완료 일자
   */
  completedDate: string;
  /**
   * 강의 ID
   */
  courseId: number;
}
export interface ClientApiV2DashboardSkills {
  data: Maybe<UserSkillsResponseDto>;
}
export interface UserSkillsResponseDto {
  /**
   * 차트 x축 분류 라벨
   */
  category: string[];
  /**
   * 차트 x축 분류 라벨(숫자만 응답되는 필드)
   */
  categories: number[];
  /**
   * 스킬 태그 차트 데이터
   */
  series: UserSkillChart;
  /**
   * 스킬 태그 목록
   */
  tags: SkillItem[];
}
export interface UserSkillChart {
  /**
   * 획득 예정 차트 데이터
   */
  seedRatings: number[];
  /**
   * 보유 차트 데이터
   */
  skillUpRatings: number[];
}
export interface SkillItem {
  /**
   * 스킬 이름
   */
  name: string;
  /**
   * 스킬 레벨 (중복된 스킬명 개수)
   */
  level: number;
}
export interface ClientApiV2DashboardJobPosts {
  data: RecommendJobPostResponseDto[];
}
export interface RecommendJobPostResponseDto {
  /**
   * 채용 공고 ID
   */
  id: number;
  /**
   * 채용 공고 제목
   */
  title: string;
  /**
   * 채용 경력 구분
   */
  jobLevel: string;
  /**
   * 채용 경력 구분
   */
  jobLevels: string[];
  /**
   * 회사명
   */
  companyName: string;
  /**
   * 회사 위치
   */
  addressRegion: string;
  /**
   * 채용 공고 대표 이미지 url
   */
  companyRepresentativeImage: string;
  /**
   * 랠릿 채용 공고 바로가기 url
   */
  url: string;
  /**
   * 입사 축하금
   */
  joinReward: number;
  /**
   * 채용 공고 요구 기술 목록
   */
  jobSkillKeywords: string[];
}
export interface ClientApiV2DashboardWeekly {
  data: Maybe<WeeklySummaryResponseDto>;
}
export interface WeeklySummaryResponseDto {
  /**
   * 일별 학습 통계 데이터
   */
  dayOfWeek: DailySummary[];
  /**
   * 주간 학습 통계 요약
   */
  summary: WeeklyTotalSummary;
}
export interface DailySummary {
  /**
   * 일별 날짜
   */
  date: string;
  /**
   * 수강한 유닛의 개수
   */
  unitCount: number;
  /**
   * 수강한 유닛의 재생시간
   */
  playTime: TimeDto;
  /**
   * 작성한 모든 노트의 개수
   */
  noteCount: number;
  /**
   * 재생 시간
   */
  playingDuration: TimeDto;
}
export interface WeeklyTotalSummary {
  /**
   * 작성한 모든 노트의 개수
   */
  totalNoteCount: number;
  /**
   * 수강한 모든 유닛의 재생시간
   */
  totalPlayTime: TimeDto;
  /**
   * 수강한 모든 유닛의 개수
   */
  totalUnitCount: number;
}
export interface ClientApiV2DashboardRoadmaps {
  data: RoadmapCardResponseDto[];
}
export interface RoadmapCardResponseDto {
  /**
   * 로드맵 ID
   */
  id: number;
  /**
   * 로드맵 제목
   */
  title: string;
  /**
   * 로드맵 진도율
   */
  progress: number;
  thumbnail: null | string;
}
export interface ClientApiV1CoachesWeeklyGoal {
  data: Maybe<WeeklyGoalResponseDto>;
}
export interface WeeklyGoalResponseDto {
  /**
   * 목표 완강한 유닛 개수
   */
  completedUnit: number;
  /**
   * 목표 총 학습시간
   */
  totalLearningDuration: number;
  /**
   * 목표 작성 노트 개수
   */
  postedNote: number;
}
export interface ClientApiV1CoachesWeeklyGoalUpdate {
  data: Maybe<UpdateWeeklyGoalResponseDto>;
}
export interface UpdateWeeklyGoalResponseDto {
  /**
   * 목표 완강한 유닛 개수
   */
  completedUnit: number;
  /**
   * 목표 총 학습시간
   */
  totalLearningDuration: number;
  /**
   * 목표 작성 노트 개수
   */
  postedNote: number;
}
export type ActivityLogEnumType =
  | 'COURSE_COMPLETE'
  | 'COURSE_REVIEW'
  | 'UNIT_COMPLETE'
  | 'SUBMISSION_SUBMITTED'
  | 'SUBMISSION_COMPLETED'
  | 'COURSE_COMPLETE_CHALLENGE_EVENT'
  | 'MESSAGE';
export interface ClientApiV1UsersActivityLogs {
  data: Maybe<UserActivityLogResponseDto>;
}
export interface UserActivityLogResponseDto {
  user: UserDto;
  logs: LogDto[];
  metadata: MetadataDto;
}
export interface UserDto {
  id: number;
  nickname: string;
  thumbnail: string;
}
export interface LogDto {
  id: string;
  /**
   * 성장 로그 타입, SUBMISSION_SUBMITTED, SUBMISSION_COMPLETED 은 SubmissionLogDto를 사용해주세요
   */
  type: ActivityLogEnumType;
  body:
    | UserMessageLogDto
    | CourseCompleteLogDto
    | UnitCompleteLogDto
    | CourseReviewLogDto
    | SubmissionLogDto
    | CourseCompleteChallengeEventLogDto;
  createdAt: string;
}
export interface UserMessageLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
  message: string;
}
export interface CourseCompleteLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
}
export interface UnitCompleteLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
  unitId: number;
  unitTitle: string;
}
export interface CourseReviewLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
}
export interface SubmissionLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
  missionTitle: string;
  missionUnitId: number;
}
export interface CourseCompleteChallengeEventLogDto {
  courseId: number;
  courseTitle: string;
  courseSlug: string;
  /**
   * lastUnitId 필드를 사용해주세요
   */
  recentUnitId: number;
  lastUnitId: number;
  message: string;
}
export interface MetadataDto {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPage: number;
  cursor: string;
}
export interface ClientApiV1UsersActivityLogsCourses {
  data: Maybe<ActivityLogCoursesResponseDto>;
}
export interface ActivityLogCoursesResponseDto {
  courses: ActivityLogCourseDto[];
}
export interface ActivityLogCourseDto {
  id: number;
  title: string;
}
export interface ClientApiV1ActivityLogsCoursesCourseId {
  data: Maybe<CourseActivityLogResponseDto>;
}
export interface CourseActivityLogResponseDto {
  logs: ActivityLogResponseDto[];
  /**
   * 최신 데이터 추가 조회용 cursor
   */
  recentlyCursor: string;
  hasNext: boolean;
  hasPrevious: boolean;
}
export interface ActivityLogResponseDto {
  /**
   * 로그 id
   */
  id: string;
  user: ActivityLogUserResponseDto;
  type: ActivityLogEnumType;
  body:
    | CourseCompleteLogResponseDto
    | UnitCompleteLogResponseDto
    | UserMessageLogResponseDto
    | CourseReviewLogResponseDto
    | SubmissionLogResponseDto
    | CourseCompleteChallengeEventLogResponseDto;
  isOwned: boolean;
  createdAt: string;
}
export interface ActivityLogUserResponseDto {
  nickname: string;
  thumbnail: string;
}
export interface CourseCompleteLogResponseDto {
  courseId: number;
}
export interface UnitCompleteLogResponseDto {
  unitId: number;
  unitTitle: string;
}
export interface UserMessageLogResponseDto {
  message: string;
}
export interface CourseReviewLogResponseDto {
  userId: number;
  courseId: number;
}
export interface SubmissionLogResponseDto {
  missionTitle: string;
}
export interface CourseCompleteChallengeEventLogResponseDto {
  message: string;
}
export interface ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDelete {
  data: Maybe<DeleteActivityUserMessageResponseDto>;
}
export interface DeleteActivityUserMessageResponseDto {
  id: string;
}
export type ActivityLogRoleCode = 'SYSTEM' | 'USER';
export type ActivityLogRoleName = '시스템' | '유저';
export interface ClientApiV1ActivityLogsCategories {
  data: Maybe<ActivityLogCategoriesResponseDto>;
}
export interface ActivityLogCategoriesResponseDto {
  categories: ActivityLogCategoryItem[];
}
export interface ActivityLogCategoryItem {
  code: ActivityLogRoleCode;
  name: ActivityLogRoleName;
}
export interface ClientApiV1Guides {
  data: Maybe<GuideResponse>;
}
export interface GuideResponse {
  /**
   * 사이트 이용규칙(약관, 처리방침 등) 내용
   */
  content: string;
}
export interface ClientApiV1GamesResultsCreate {
  data: Maybe<CreateGameResultResponse>;
}
export interface CreateGameResultResponse {
  /**
   * 게임 결과 ID
   */
  resultId: number;
}
export interface ClientApiV1GamesResultsResultIdUpdate {
  data: Maybe<String>;
}
export interface ClientApiV1GamesStats {
  data: GameStatResponse[];
}
export interface GameStatResponse {
  /**
   * 게임 결과 ID
   */
  id: number;
  /**
   * 사용자 닉네임
   */
  userNickname: string;
  /**
   * 점수
   */
  score: number;
  /**
   * 게임 유형
   */
  gameType: 'MOLE_ATTACK' | 'GET_OFF_WORK' | 'SLOT_DRESS_UP';
  /**
   * 코멘트
   */
  comment: string;
  /**
   * 이미지 CDN URL (SLOT_DRESS_UP 게임 전용)
   */
  imageUrl: string;
  /**
   * 생성일시
   */
  createdAt: string;
}
export type ClientApiV1AccountsSlugConfirmUpdateResponse = ApiResponse<
  ClientApiV1AccountsSlugConfirmUpdate['data']
>;
export type ClientApiV1AccountsMyProfileResponse = ApiResponse<
  ClientApiV1AccountsMyProfile['data']
>;
export type ClientApiV1AccountsMyProfileUpdateResponse = ApiResponse<
  ClientApiV1AccountsMyProfileUpdate['data']
>;
export type ClientApiV1AccountsProfileImageCreateResponse = ApiResponse<
  ClientApiV1AccountsProfileImageCreate['data']
>;
export type ClientApiV1AccountsCredentialsResponse = ApiResponse<
  ClientApiV1AccountsCredentials['data']
>;
export type ClientApiV1AccountsInventoryResponse = ApiResponse<
  ClientApiV1AccountsInventory['data']
>;
export type ClientApiV1AccountsNotificationResponse = ApiResponse<
  ClientApiV1AccountsNotification['data']
>;
export type ClientApiV1AccountsNotificationUpdateResponse = ApiResponse<
  ClientApiV1AccountsNotificationUpdate['data']
>;
export type ClientApiV1AccountsNotificationPublicResponse = ApiResponse<
  ClientApiV1AccountsNotificationPublic['data']
>;
export type ClientApiV1AccountsNotificationPublicUpdateResponse = ApiResponse<
  ClientApiV1AccountsNotificationPublicUpdate['data']
>;
export type ClientApiV2AccountsProfileResponse = ApiResponse<ClientApiV2AccountsProfile['data']>;
export type ClientApiV1SessionsResponse = ApiResponse<ClientApiV1Sessions['data']>;
export type ClientApiV1AuthSignoutResponse = ApiResponse<ClientApiV1AuthSignout['data']>;
export type ClientApiV1AuthWithdrawCreateResponse = ApiResponse<
  ClientApiV1AuthWithdrawCreate['data']
>;
export type ClientApiV1AuthChangeEmailVerificationCreateResponse = ApiResponse<
  ClientApiV1AuthChangeEmailVerificationCreate['data']
>;
export type ClientApiV1AuthChangeEmailCreateResponse = ApiResponse<
  ClientApiV1AuthChangeEmailCreate['data']
>;
export type ClientApiV1AuthChangePasswordVerificationEmailCreateResponse = ApiResponse<
  ClientApiV1AuthChangePasswordVerificationEmailCreate['data']
>;
export type ClientApiV1AuthChangePasswordVerificationTokenCreateResponse = ApiResponse<
  ClientApiV1AuthChangePasswordVerificationTokenCreate['data']
>;
export type ClientApiV1AuthChangePasswordByTokenUpdateResponse = ApiResponse<
  ClientApiV1AuthChangePasswordByTokenUpdate['data']
>;
export type ClientApiV1AuthChangePasswordUpdateResponse = ApiResponse<
  ClientApiV1AuthChangePasswordUpdate['data']
>;
export type ClientApiV1AuthPhoneVerificationCreateResponse = ApiResponse<
  ClientApiV1AuthPhoneVerificationCreate['data']
>;
export type ClientApiV1AuthPhoneVerificationPublicCreateResponse = ApiResponse<
  ClientApiV1AuthPhoneVerificationPublicCreate['data']
>;
export type ClientApiV1AuthPhoneVerificationCheckCreateResponse = ApiResponse<
  ClientApiV1AuthPhoneVerificationCheckCreate['data']
>;
export type ClientApiV1AuthChangePhoneNumberCreateResponse = ApiResponse<
  ClientApiV1AuthChangePhoneNumberCreate['data']
>;
export type ClientApiV1AuthSsoRedirectResponse = ApiResponse<ClientApiV1AuthSsoRedirect['data']>;
export type ClientApiV1AuthSignupVerifyCreateResponse = ApiResponse<
  ClientApiV1AuthSignupVerifyCreate['data']
>;
export type ClientApiV2AuthSigninCreateResponse = ApiResponse<ClientApiV2AuthSigninCreate['data']>;
export type ClientApiV2AuthSignupCreateResponse = ApiResponse<ClientApiV2AuthSignupCreate['data']>;
export type ClientApiV2AuthSignupVerifyEmailCreateResponse = ApiResponse<
  ClientApiV2AuthSignupVerifyEmailCreate['data']
>;
export type ClientApiV1DashboardsRecentLearningResponse = ApiResponse<
  ClientApiV1DashboardsRecentLearning['data']
>;
export type ClientApiV1DashboardsMyNotesResponse = ApiResponse<
  ClientApiV1DashboardsMyNotes['data']
>;
export type ClientApiV1DashboardsMyQuestionsResponse = ApiResponse<
  ClientApiV1DashboardsMyQuestions['data']
>;
export type ClientApiV2DashboardAnnualLearningResponse = ApiResponse<
  ClientApiV2DashboardAnnualLearning['data']
>;
export type ClientApiV2DashboardCertificatesResponse = ApiResponse<
  ClientApiV2DashboardCertificates['data']
>;
export type ClientApiV2DashboardSkillsResponse = ApiResponse<ClientApiV2DashboardSkills['data']>;
export type ClientApiV2DashboardJobPostsResponse = ApiResponse<
  ClientApiV2DashboardJobPosts['data']
>;
export type ClientApiV2DashboardWeeklyResponse = ApiResponse<ClientApiV2DashboardWeekly['data']>;
export type ClientApiV2DashboardRoadmapsResponse = ApiResponse<
  ClientApiV2DashboardRoadmaps['data']
>;
export type ClientApiV1CoachesWeeklyGoalResponse = ApiResponse<
  ClientApiV1CoachesWeeklyGoal['data']
>;
export type ClientApiV1CoachesWeeklyGoalUpdateResponse = ApiResponse<
  ClientApiV1CoachesWeeklyGoalUpdate['data']
>;
export type ClientApiV1UsersActivityLogsResponse = ApiResponse<
  ClientApiV1UsersActivityLogs['data']
>;
export type ClientApiV1UsersActivityLogsCoursesResponse = ApiResponse<
  ClientApiV1UsersActivityLogsCourses['data']
>;
export type ClientApiV1ActivityLogsCoursesCourseIdResponse = ApiResponse<
  ClientApiV1ActivityLogsCoursesCourseId['data']
>;
export type ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteResponse = ApiResponse<
  ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDelete['data']
>;
export type ClientApiV1ActivityLogsCategoriesResponse = ApiResponse<
  ClientApiV1ActivityLogsCategories['data']
>;
export type ClientApiV1GuidesResponse = ApiResponse<ClientApiV1Guides['data']>;
export type ClientApiV1GamesResultsCreateResponse = ApiResponse<
  ClientApiV1GamesResultsCreate['data']
>;
export type ClientApiV1GamesResultsResultIdUpdateResponse = ApiResponse<
  ClientApiV1GamesResultsResultIdUpdate['data']
>;
export type ClientApiV1GamesStatsResponse = ApiResponse<ClientApiV1GamesStats['data']>;

/**
 * created by DTOGenerator
 */

export interface AccountSlugConfirmRequest {
  /**
   * 슬러그 값
   */
  slug: string;
}
export interface MyProfileUpdateRequest {
  /**
   * 닉네임
   */
  nickname: string;
  /**
   * 프로필 이미지 파일 ID
   */
  profileImageFileId: null | number;
  /**
   * 자기소개
   */
  introduce: null | string;
  /**
   * 슬러그
   */
  slug: null | string;
}
export interface UpdateMyNotificationSettingRequestDto {
  status: boolean;
  /**
   * 알림 타입
   */
  type: 'SERVICE_ANNOUNCEMENT' | 'COURSE_NEW_NEWS' | 'COMMUNITY_COMMENT' | 'MARKETING';
}
export type EmailTokenType = 'ENCRYPT' | 'MAILTOKEN';
export interface UpdateMyNotificationSettingWithTokenRequestDto {
  status: boolean;
  /**
   * 알림 타입
   */
  type: 'SERVICE_ANNOUNCEMENT' | 'COURSE_NEW_NEWS' | 'COMMUNITY_COMMENT' | 'MARKETING';
  /**
   * 토큰
   */
  token: string;
  tokenType?: EmailTokenType;
}
export interface WithdrawalAccountRequestDto {
  password: string;
}
export interface ChangeEmailVerificationMailRequest {
  email: string;
}
export interface ChangeEmailRequest {
  /**
   * 이메일로 전달된 인증키
   */
  validationKey: string;
}
export interface ChangePasswordVerificationMailRequest {
  email: string;
  /**
   * 비밀번호 변경 후 이동할 URL. (ex: https://www.rallit.com)
   */
  referUrl: string;
}
export interface ChangePasswordTokenVerificationRequest {
  /**
   * 비밀번호 변경을 위한 인증키
   */
  validationKey: string;
}
export interface ChangePasswordWithoutSessionRequest {
  /**
   * 비밀번호를 변경할 계정의 이메일 주소
   */
  email: string;
  /**
   * 새로운 비밀번호
   */
  newPassword: string;
  /**
   * 비밀번호 변경을 위한 인증키
   */
  validationKey: string;
}
export interface ChangePasswordRequest {
  /**
   * 기존 비밀번호
   */
  oldPassword: string;
  /**
   * 새로운 비밀번호
   */
  newPassword: string;
}
export interface SendPhoneVerificationCodeRequest {
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
}
export interface SendPhoneVerificationCodeCaptchaRequest {
  /**
   * Captcha 토큰
   */
  captchaToken: string;
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
}
export interface CheckPhoneVerificationCodeRequest {
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
  /**
   * 인증 코드
   */
  verifyCode: string;
}
export interface ChangePhoneNumberRequest {
  /**
   * 인증 코드
   */
  authCode: string;
  /**
   * 국가 코드
   */
  countryCode: string;
  /**
   * 휴대폰 번호
   */
  phoneNumber: string;
}
export interface SignUpVerifyRequest {
  /**
   * 회원가입 인증 메일로 전달한 인증키
   */
  validationKey: string;
  /**
   * 회원가입 시 생성한 users_access_referer_id. 인증 메일 링크에 포함되어 있음.
   */
  usersAccessRefererId?: number;
}
export interface SignInEmailAndPasswordRequestV2 {
  email: string;
  password: string;
}
export interface SignupEmailAndPasswordRequestV2 {
  /**
   * Captcha 토큰
   */
  captchaToken: string;
  /**
   * 이메일
   */
  email: string;
  /**
   * 비밀번호
   */
  password: string;
  /**
   * 마케팅 동의 여부
   */
  allowedMarketing: boolean;
  /**
   * 클라이언트 정보
   */
  clientReferrer?: ClientReferrerRequest;
}
export interface ClientReferrerRequest {
  /**
   * 접속 referrer
   */
  referrer?: string;
  /**
   * document referrer
   */
  documentReferrer?: string;
  /**
   * 접속 브라우저
   */
  browser?: string;
  /**
   * 접속 os
   */
  os?: string;
  /**
   * 접속 url
   */
  accessUrl?: string;
  /**
   * 접속 url 쿼리 정보
   */
  queryParam?: never;
  /**
   * utm campaign 파라미터
   */
  utmCampaign?: string;
  /**
   * utm medium 파라미터
   */
  utmMedium?: string;
  /**
   * utm source 파라미터
   */
  utmSource?: string;
  /**
   * utm content 파라미터
   */
  utmContent?: string;
  /**
   * utm term 파라미터
   */
  utmTerm?: string;
}
export interface SendBySignupVerifyEmailRequest {
  /**
   * 이메일
   */
  email: string;
}
export interface UpdateWeeklyGoalRequestDto {
  /**
   * 목표 완강한 유닛 개수
   */
  completedUnit: number;
  /**
   * 목표 총 학습시간
   */
  totalLearningDuration: number;
  /**
   * 목표 작성 노트 개수
   */
  postedNote: number;
}
export interface CreateGameResultRequest {
  /**
   * 게임 점수
   */
  score: number;
  /**
   * 게임 유형
   */
  gameType: 'MOLE_ATTACK' | 'GET_OFF_WORK' | 'SLOT_DRESS_UP';
  /**
   * 코멘트
   */
  comment?: string;
  /**
   * 이미지 CDN URL (SLOT_DRESS_UP 게임 전용)
   */
  imageUrl?: string;
}
export interface UpdateGameResultRequest {
  /**
   * 코멘트
   */
  comment?: string;
  /**
   * 이미지 CDN URL (SLOT_DRESS_UP 게임 전용)
   */
  imageUrl?: string;
}
export interface ClientApiV1AccountsSlugConfirmUpdateVariables {
  /**
   * 슬러그 값
   */
  slug: string;
}
export interface ClientApiV1AccountsMyProfileUpdateVariables {
  /**
   * 닉네임
   */
  nickname: string;
  /**
   * 프로필 이미지 파일 ID
   */
  profileImageFileId: null | number;
  /**
   * 자기소개
   */
  introduce: null | string;
  /**
   * 슬러그
   */
  slug: null | string;
}
export interface ClientApiV1AccountsProfileImageCreateVariables {
  [k: string]: unknown;
}
export interface ClientApiV1AccountsNotificationUpdateVariables {
  status: boolean;
  /**
   * 알림 타입
   */
  type: 'SERVICE_ANNOUNCEMENT' | 'COURSE_NEW_NEWS' | 'COMMUNITY_COMMENT' | 'MARKETING';
}
export interface ClientApiV1AccountsNotificationPublicQueryString {
  token: string;
  tokenType?: EmailTokenType & string;
}
export interface ClientApiV1AccountsNotificationPublicUpdateVariables {
  status: boolean;
  /**
   * 알림 타입
   */
  type: 'SERVICE_ANNOUNCEMENT' | 'COURSE_NEW_NEWS' | 'COMMUNITY_COMMENT' | 'MARKETING';
  /**
   * 토큰
   */
  token: string;
  tokenType?: EmailTokenType;
}
export interface ClientApiV1AuthSignoutQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  redirectTo?: string;
}
export interface ClientApiV1AuthWithdrawCreateVariables {
  password: string;
}
export interface ClientApiV1AuthChangeEmailVerificationCreateVariables {
  email: string;
}
export interface ClientApiV1AuthChangeEmailCreateVariables {
  /**
   * 이메일로 전달된 인증키
   */
  validationKey: string;
}
export interface ClientApiV1AuthChangePasswordVerificationEmailCreateVariables {
  email: string;
  /**
   * 비밀번호 변경 후 이동할 URL. (ex: https://www.rallit.com)
   */
  referUrl: string;
}
export interface ClientApiV1AuthChangePasswordVerificationTokenCreateVariables {
  /**
   * 비밀번호 변경을 위한 인증키
   */
  validationKey: string;
}
export interface ClientApiV1AuthChangePasswordByTokenUpdateVariables {
  /**
   * 비밀번호를 변경할 계정의 이메일 주소
   */
  email: string;
  /**
   * 새로운 비밀번호
   */
  newPassword: string;
  /**
   * 비밀번호 변경을 위한 인증키
   */
  validationKey: string;
}
export interface ClientApiV1AuthChangePasswordUpdateVariables {
  /**
   * 기존 비밀번호
   */
  oldPassword: string;
  /**
   * 새로운 비밀번호
   */
  newPassword: string;
}
export interface ClientApiV1AuthPhoneVerificationCreateVariables {
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
}
export interface ClientApiV1AuthPhoneVerificationPublicCreateVariables {
  /**
   * Captcha 토큰
   */
  captchaToken: string;
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
}
export interface ClientApiV1AuthPhoneVerificationCheckCreateVariables {
  /**
   * 휴대폰 번호
   */
  phone: string;
  /**
   * 국가 코드
   */
  countryCode: string;
  /**
   * 인증 코드
   */
  verifyCode: string;
}
export interface ClientApiV1AuthChangePhoneNumberCreateVariables {
  /**
   * 인증 코드
   */
  authCode: string;
  /**
   * 국가 코드
   */
  countryCode: string;
  /**
   * 휴대폰 번호
   */
  phoneNumber: string;
}
export interface ClientApiV1AuthSsoRedirectQueryString {
  referUrl?: string;
  goormId?: string;
  groupId?: number;
  redirectUrl?: string;
}
export interface ClientApiV1AuthSignupVerifyCreateVariables {
  /**
   * 회원가입 인증 메일로 전달한 인증키
   */
  validationKey: string;
  /**
   * 회원가입 시 생성한 users_access_referer_id. 인증 메일 링크에 포함되어 있음.
   */
  usersAccessRefererId?: number;
}
export interface ClientApiV2AuthSigninCreateVariables {
  email: string;
  password: string;
}
export interface ClientApiV2AuthSignupCreateVariables {
  /**
   * Captcha 토큰
   */
  captchaToken: string;
  /**
   * 이메일
   */
  email: string;
  /**
   * 비밀번호
   */
  password: string;
  /**
   * 마케팅 동의 여부
   */
  allowedMarketing: boolean;
  /**
   * 클라이언트 정보
   */
  clientReferrer?: ClientReferrerRequest;
}
export interface ClientApiV2AuthSignupVerifyEmailCreateVariables {
  /**
   * 이메일
   */
  email: string;
}
export interface ClientApiV1DashboardsRecentLearningQueryString {
  lang?:
    | { __enumName__: 'KO'; _code: 'ko' }
    | { __enumName__: 'EN'; _code: 'en' }
    | { __enumName__: 'JA'; _code: 'ja' }
    | { __enumName__: 'VI'; _code: 'vi' };
  user_id?: number;
  userId?: number;
}
export interface ClientApiV1DashboardsMyNotesQueryString {
  lang?:
    | { __enumName__: 'KO'; _code: 'ko' }
    | { __enumName__: 'EN'; _code: 'en' }
    | { __enumName__: 'JA'; _code: 'ja' }
    | { __enumName__: 'VI'; _code: 'vi' };
  user_id?: number;
  userId?: number;
}
export interface ClientApiV1DashboardsMyQuestionsQueryString {
  user_id?: number;
  userId?: number;
}
export interface ClientApiV2DashboardAnnualLearningQueryString {
  year: number;
  timezone: string;
  user_id?: number;
  userId?: number;
}
export interface ClientApiV2DashboardCertificatesQueryString {
  user_id?: number;
  userId?: number;
}
export interface ClientApiV2DashboardSkillsQueryString {
  user_id?: number;
  userId?: number;
  lang?:
    | { __enumName__: 'KO'; _code: 'ko' }
    | { __enumName__: 'EN'; _code: 'en' }
    | { __enumName__: 'JA'; _code: 'ja' }
    | { __enumName__: 'VI'; _code: 'vi' };
}
export interface ClientApiV2DashboardJobPostsQueryString {
  user_id?: number;
  userId?: number;
}
export interface ClientApiV2DashboardWeeklyQueryString {
  date?: string;
  user_id?: number;
  userId?: number;
}
export interface ClientApiV2DashboardRoadmapsQueryString {
  user_id?: number;
  userId?: number;
}
export interface ClientApiV1CoachesWeeklyGoalQueryString {
  date?: string;
  user_id?: number;
  userId?: number;
}
export interface ClientApiV1CoachesWeeklyGoalUpdateVariables {
  /**
   * 목표 완강한 유닛 개수
   */
  completedUnit: number;
  /**
   * 목표 총 학습시간
   */
  totalLearningDuration: number;
  /**
   * 목표 작성 노트 개수
   */
  postedNote: number;
}
export type UserActivityLogTypeRequestEnum = 'SYSTEM' | 'USER' | 'ALL';
export interface ClientApiV1UsersActivityLogsQueryString {
  pageNumber?: number;
  pageSize?: number;
  courseIds?: string;
  logType?: UserActivityLogTypeRequestEnum & string;
  fromDate?: string;
  toDate?: string;
  cursor?: string;
  previousPageNumber?: number;
  user_id?: number;
  userId?: number;
}
export interface ClientApiV1ActivityLogsCoursesCourseIdQueryString {
  first?: number;
  after?: string;
  last?: number;
  before?: string;
}
export interface ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteUrlParams {
  messageId: string;
  courseId: string;
}
export interface ClientApiV1ActivityLogsCoursesCourseIdMessagesMessageIdDeleteVariables {
  [k: string]: unknown;
}
export interface ClientApiV1GuidesQueryString {
  lang?: 'ko' | 'en' | 'ja' | 'vi';
  type:
    | 'POLICY'
    | 'TERMS'
    | 'PRIVACY'
    | 'MARKETING_PARTNERS_TERMS'
    | 'MARKETING_PARTNERS_POLICY'
    | 'MARKETING_PARTNERS_GUIDE'
    | 'TRADE_LAW';
  period?: 'PREVIOUS' | 'CURRENT';
}
export interface ClientApiV1GamesResultsCreateVariables {
  /**
   * 게임 점수
   */
  score: number;
  /**
   * 게임 유형
   */
  gameType: 'MOLE_ATTACK' | 'GET_OFF_WORK' | 'SLOT_DRESS_UP';
  /**
   * 코멘트
   */
  comment?: string;
  /**
   * 이미지 CDN URL (SLOT_DRESS_UP 게임 전용)
   */
  imageUrl?: string;
}
export interface ClientApiV1GamesResultsResultIdUpdateUrlParams {
  resultId: number;
}
export interface ClientApiV1GamesResultsResultIdUpdateVariables {
  /**
   * 코멘트
   */
  comment?: string;
  /**
   * 이미지 CDN URL (SLOT_DRESS_UP 게임 전용)
   */
  imageUrl?: string;
}
export interface ClientApiV1GamesStatsQueryString {
  gameType: 'MOLE_ATTACK' | 'GET_OFF_WORK' | 'SLOT_DRESS_UP';
  period?: 'daily' | 'monthly' | 'all';
}
